import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  addOpsIncidentNote,
  getOpsIncident,
  getOpsIncidents,
  patchOpsIncident,
  type AdminOpsIncident,
  type AdminOpsIncidentDetail,
  type AdminOpsIncidentSeverity,
  type AdminOpsIncidentSource,
  type AdminOpsIncidentStatus,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";
import { cn } from "@admin/lib/utils";

const STATUSES: Array<AdminOpsIncidentStatus | "all"> = ["all", "open", "resolved", "ignored"];
const SOURCES: Array<AdminOpsIncidentSource | "all"> = [
  "all",
  "api",
  "vm",
  "lifecycle",
  "cron",
];
const SEVERITIES: Array<AdminOpsIncidentSeverity | "all"> = [
  "all",
  "critical",
  "error",
  "warn",
  "info",
];

function statusClass(status: string): string {
  if (status === "open") return "bg-amber-500/20 text-amber-200";
  if (status === "resolved") return "bg-emerald-500/20 text-emerald-300";
  return "bg-muted text-muted-fg";
}

function severityClass(severity: string): string {
  if (severity === "critical") return "bg-danger/20 text-danger";
  if (severity === "error") return "bg-danger/20 text-danger";
  if (severity === "warn") return "bg-amber-500/20 text-amber-200";
  return "bg-muted text-muted-fg";
}

function sourceLabel(source: string): string {
  if (source === "api") return "API";
  if (source === "vm") return "Workspace";
  if (source === "lifecycle") return "Lifecycle";
  if (source === "cron") return "Scheduled job";
  return source;
}

function IncidentDetailPanel({
  detail,
  onUpdated,
}: {
  detail: AdminOpsIncidentDetail;
  onUpdated: (next: AdminOpsIncidentDetail) => void;
}) {
  const [note, setNote] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState(detail.incident.resolutionNotes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<AdminOpsIncidentDetail>) => {
    setBusy(true);
    setErr(null);
    try {
      const next = await fn();
      onUpdated(next);
      setResolutionNotes(next.incident.resolutionNotes ?? "");
      setNote("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-fg">{detail.incident.message}</div>
      {detail.incident.sentryUrl && (
        <a
          href={detail.incident.sentryUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-accent hover:underline"
        >
          Open in Sentry
        </a>
      )}
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-muted/30 p-3 font-mono text-xs text-muted-fg">
        {JSON.stringify(detail.incident.detail, null, 2)}
      </pre>

      {detail.incident.resolutionNotes && (
        <div className="rounded-md border border-border p-3 text-sm">
          <div className="text-xs font-medium text-muted-fg">Resolution notes</div>
          <p className="mt-1 whitespace-pre-wrap">{detail.incident.resolutionNotes}</p>
        </div>
      )}

      {detail.notes.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-fg">Timeline notes</div>
          {detail.notes.map((n) => (
            <div key={n.id} className="rounded-md border border-border p-2 text-sm">
              <div className="text-xs text-muted-fg">{formatDate(n.createdAt)}</div>
              <p className="mt-1 whitespace-pre-wrap">{n.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <label className="text-xs text-muted-fg" htmlFor={`resolution-${detail.incident.id}`}>
          Resolution notes (saved with resolve/ignore)
        </label>
        <textarea
          id={`resolution-${detail.incident.id}`}
          className="min-h-[72px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {detail.incident.status !== "resolved" && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              void run(() =>
                patchOpsIncident(detail.incident.id, {
                  status: "resolved",
                  resolutionNotes: resolutionNotes || undefined,
                }),
              )
            }
          >
            Mark resolved
          </Button>
        )}
        {detail.incident.status !== "ignored" && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void run(() =>
                patchOpsIncident(detail.incident.id, {
                  status: "ignored",
                  resolutionNotes: resolutionNotes || undefined,
                }),
              )
            }
          >
            Ignore
          </Button>
        )}
        {detail.incident.status !== "open" && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void run(() => patchOpsIncident(detail.incident.id, { status: "open" }))}
          >
            Reopen
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-xs text-muted-fg" htmlFor={`note-${detail.incident.id}`}>
          Add note
        </label>
        <textarea
          id={`note-${detail.incident.id}`}
          className="min-h-[72px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={busy || note.trim().length === 0}
          onClick={() => void run(() => addOpsIncidentNote(detail.incident.id, note.trim()))}
        >
          Save note
        </Button>
      </div>

      {err && <p className="text-sm text-danger">{err}</p>}
    </div>
  );
}

function IncidentRow({
  incident,
  onListPatch,
}: {
  incident: AdminOpsIncident;
  onListPatch: (id: string, patch: Partial<AdminOpsIncident>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AdminOpsIncidentDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadDetail = async () => {
    setLoadingDetail(true);
    try {
      const d = await getOpsIncident(incident.id);
      setDetail(d);
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) void loadDetail();
  };

  return (
    <>
      <tr
        className="cursor-pointer border-t border-border hover:bg-muted/30"
        onClick={() => void toggle()}
      >
        <td className="px-3 py-2">
          <span
            className={cn("inline-block rounded-full px-2 py-0.5 text-xs", statusClass(incident.status))}
          >
            {incident.status}
          </span>
        </td>
        <td className="px-3 py-2">
          <span
            className={cn(
              "inline-block rounded-full px-2 py-0.5 text-xs",
              severityClass(incident.severity),
            )}
          >
            {incident.severity}
          </span>
        </td>
        <td className="px-3 py-2 text-xs text-muted-fg">{sourceLabel(incident.source)}</td>
        <td className="px-3 py-2 font-mono text-xs">{incident.kind}</td>
        <td className="px-3 py-2">{incident.title}</td>
        <td className="px-3 py-2 tabular-nums text-xs">{incident.occurrenceCount}</td>
        <td className="px-3 py-2 text-xs text-muted-fg">{formatDate(incident.lastSeenAt)}</td>
        <td className="px-3 py-2 text-xs">
          {incident.userId ? (
            <Link
              to={`/users/${incident.userId}`}
              className="text-accent hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {incident.userId.slice(0, 8)}...
            </Link>
          ) : (
            "-"
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={8} className="px-3 py-4">
            {loadingDetail && !detail ? (
              <p className="text-sm text-muted-fg">Loading detail...</p>
            ) : detail ? (
              <IncidentDetailPanel
                detail={detail}
                onUpdated={(next) => {
                  setDetail(next);
                  onListPatch(incident.id, next.incident);
                }}
              />
            ) : (
              <p className="text-sm text-danger">Could not load incident detail.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function IncidentsPage() {
  const [incidents, setIncidents] = useState<AdminOpsIncident[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AdminOpsIncidentStatus | "all">("open");
  const [sourceFilter, setSourceFilter] = useState<AdminOpsIncidentSource | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<AdminOpsIncidentSeverity | "all">("all");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await getOpsIncidents({
        status: statusFilter === "all" ? undefined : statusFilter,
        source: sourceFilter === "all" ? undefined : sourceFilter,
        severity: severityFilter === "all" ? undefined : severityFilter,
        limit: 100,
      });
      setIncidents(res.incidents);
      setTotal(res.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load incidents");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, severityFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchListItem = (id: string, patch: Partial<AdminOpsIncident>) => {
    setIncidents((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Incidents</h1>
        <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </div>

      <p className="text-sm text-muted-fg">
        Curated operational issues from API errors, workspace relays, scheduled jobs, and lifecycle
        alerts. Deduped by
        fingerprint; expand a row to resolve, ignore, or add notes.
      </p>

      {err && <p className="text-danger">{err}</p>}

      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs text-muted-fg">Status:</span>
        {STATUSES.map((f) => (
          <Button
            key={f}
            variant={statusFilter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(f)}
          >
            {f}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs text-muted-fg">Source:</span>
        {SOURCES.map((f) => (
          <Button
            key={f}
            variant={sourceFilter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setSourceFilter(f)}
          >
            {f === "all" ? "All" : sourceLabel(f)}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs text-muted-fg">Severity:</span>
        {SEVERITIES.map((f) => (
          <Button
            key={f}
            variant={severityFilter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setSeverityFilter(f)}
          >
            {f}
          </Button>
        ))}
      </div>

      <p className="text-xs text-muted-fg">
        Showing {incidents.length} of {total} matching incidents
      </p>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/30 text-xs text-muted-fg">
            <tr>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Severity</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Count</th>
              <th className="px-3 py-2">Last seen</th>
              <th className="px-3 py-2">User</th>
            </tr>
          </thead>
          <tbody>
            {loading && incidents.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-fg">
                  Loading...
                </td>
              </tr>
            ) : incidents.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-fg">
                  No incidents match the current filters.
                </td>
              </tr>
            ) : (
              incidents.map((inc) => (
                <IncidentRow key={inc.id} incident={inc} onListPatch={patchListItem} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
